/**
 * @param {string} type - The type of the response to be returned. Default is 'json'.
 * @returns {Object} - The configuration object for the fetch request.
 */
function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

async function fetchHTML(url) {
  const response = await fetch(`${url}`);
  return new DOMParser().parseFromString(await response.text(), 'text/html');
}

function getElementDimensions(element = null) {
  if (!element) return;

  const elementDom = document.querySelector(element);
  const rect = elementDom.getBoundingClientRect();
  return rect;
}

/**
 * Get all focusable elements inside a container
 * @param {HTMLElement} container
 * @returns {HTMLElement[]}
 */
function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  ).filter((element) => {
    // Check if element or any of its ancestors are hidden
    let current = element;
    while (current) {
      const style = window.getComputedStyle(current);
      if (style.display === 'none' || style.visibility === 'hidden') {
        return false;
      }
      current = current.parentElement;
    }
    return true;
  });
}

/**
 * Get the index of a node in its parent
 * @param {HTMLElement} node
 * @returns {number}
 */
function getNodeIndex(node) {
  return Array.from(node.parentNode.children).indexOf(node);
}

/**
 * Detects a click outside of a given element
 * @param {HTMLElement} element - The element to detect a click outside of
 * @param {Function} callback - The callback to execute when a click is detected outside of the element
 * @returns {Function} - A function to remove the event listener
 * @example
 * this.drawer = this.querySelector('drawer');
 * onClickOutside(this.drawer, () => this.close());
 */
function onClickOutside(element, callback) {
  const handleClick = (event) => {
    if (element && !element.contains(event.target)) callback();
  };

  document.addEventListener('mousedown', handleClick);

  return () => {
    document.removeEventListener('mousedown', handleClick);
  };
}

/**
 * Prefetch a link
 * @param {HTMLLinkElement} link - The link to prefetch
 * @example prefetchLink(document.querySelector('a[href="/collections/all"]'));
 */
function prefetchLink(link) {
  const prefetchLinkExists = document.querySelector(`link[href="${link.href}"]`);
  if (prefetchLinkExists) return;

  const { href } = link;
  const prefetchLink = document.createElement('link');
  prefetchLink.rel = 'prefetch';
  prefetchLink.href = href;
  document.head.appendChild(prefetchLink);
}

export { fetchConfig, fetchHTML, getElementDimensions, getFocusableElements, getNodeIndex, onClickOutside, prefetchLink };
